import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import apiClient from "../../../shared/API/apiClient";
import { useReportingYear } from "../../../context/ReportingYearContext";
import "../../../styles/pages/Scope2ActivityListing.css";

const fmt = (val, decimals = 2) =>
  val !== null && val !== undefined && val !== "" && !isNaN(Number(val))
    ? Number(val).toLocaleString("en-IN", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    : "0.00";

const Scope2ActivityListing = () => {
  const { reportingYearId } = useReportingYear();
  const location = useLocation();
  const navigate = useNavigate();
  const { title, entityId } = location.state || {};

  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!reportingYearId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await apiClient.get("/api/inner-activity-list", {
          params: { reporting_year_id: reportingYearId, scope: "scope2", legal_entity_id: entityId },
        });
        if (res.data.success) setApiData(res.data);
        else setError(res.data.message || "Failed to load data.");
      } catch (err) {
        setError("An error occurred while fetching Scope 2 data.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [reportingYearId, entityId]);

  if (loading) return <div className="scope2-container">Loading...</div>;
  if (error) return <div className="scope2-container" style={{color: 'red'}}>{error}</div>;

  const rows = apiData?.data || [];
  const grouped = apiData?.grouped || [];
  const totalValue = apiData?.total_emission ?? 0;

  return (
    <div className="scope2-container">
      

      {apiData && (
        <>
          {/* Hero Section */}
          <div className="hero-card">
            <div className="icon-badge">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
              </svg>
            </div>
            <div>
              <div className="hero-title">{title || "Scope 2 Emissions Breakdown"}</div>
              <div className="hero-subtitle">Indirect emissions from purchased electricity</div>
              <div className="hero-value">
                {fmt(totalValue, 2)}<span className="hero-unit">tCO₂e</span>
              </div>
            </div>
          </div>

          {/* Breakdown Table */}
          <div className="section-card">
            <div className="section-title">Breakdown by Source</div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th className="text-center">Consumption (kWh)</th>
                  <th className="text-center">Emission Factor (kg CO₂e / kWh)</th>
                  <th className="text-right">Emissions (tCO₂e)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => (
                  <tr key={item.entry_id}>
                    <td>{item.key_name || item.option_value || "Grid"}</td>
                    <td className="text-center">{fmt(item.value ?? item.quantity, 0)}</td>
                    <td className="text-center">{item.emission_factor || "0.00"}</td>
                    <td className="text-right" style={{ fontWeight: 600 }}>{fmt(item.emissions, 2)}</td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td colSpan={3}>Total</td>
                  <td className="text-right">{fmt(totalValue, 2)} tCO₂e</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Contribution Section */}
          <div className="section-card">
            <div className="section-title">Contribution by Source</div>
            {grouped.map((g, idx) => (
              <div key={idx} className="bar-container">
                <div className="bar-info">
                  <span>{g.option_value}</span>
                  <span>{fmt(g.total_emission, 2)} tCO₂e · {Number(g.percentage).toFixed(1)}%</span>
                </div>
                <div className="bar-track">
                  <div 
                    className="bar-fill" 
                    style={{ width: `${Math.min(g.percentage, 100)}%` }} 
                  />
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      <div className="ws-back">
<button onClick={() => navigate(-1)} className="back-link">
        ← Back to Environmental Summary
      </button>
      </div>
    </div>
  );
};

export default Scope2ActivityListing;